import { useCallback } from "react";
import { formatSearchParamsToString, useSearchParamsQuery } from "@finbackoffice/site-core";
import { CasinoGameTag } from "@finbackoffice/enums";
import { CasinoFilterPropType, CasinoGameTypeEnum, ProvidersEnum } from "@finbackoffice/fe-core";
import { useRouter } from "next/navigation";
import { ParsedUrlQuery } from "querystring";
import { RouteNames } from "utils/constants";
import { usePathnameWithLocal } from "hooks/base/usePathnameWithLocal";
import { useCasinoScope } from "./useCasinoScope";

export const useCasinoFilters = () => {
    const router = useRouter();
    const { lang } = usePathnameWithLocal();
    const { query } = useSearchParamsQuery();
    const { scope } = useCasinoScope();

    const navigateToSection = useCallback(
        (hash: string, queryFilter?: ParsedUrlQuery) => {
            const dir = `/${RouteNames.CASINO}/${scope || RouteNames.SLOTS}`;
            let q: Record<string, string | undefined | string[]> = { ...query };

            if (queryFilter) {
                // tag and filter(in SLOTS) cannot have values at same time
                if (scope !== RouteNames.LIVE_CASINO) {
                    if (queryFilter.tag) {
                        delete q.filter;
                    } else if (queryFilter.filter) {
                        delete q.tag;
                    }
                }

                q = { ...q, ...queryFilter };

                // handle clear
                if (queryFilter.tag === "") {
                    delete q.tag;
                } else if (queryFilter.filter === "") {
                    delete q.filter;
                } else if (queryFilter.provider === "") {
                    delete q.provider;
                }
            }

            if (!hash) {
                if (q.section) {
                    delete q.section;
                }
            } else {
                q.section = hash;
            }

            router.push(`/${lang}${dir}?${formatSearchParamsToString(q)}`, { scroll: false });
        },
        [lang, query, router, scope],
    );

    const onGameTypeChange = useCallback(
        (val: CasinoGameTypeEnum | "") => {
            navigateToSection(val, { filter: val });
        },
        [navigateToSection],
    );

    const onGamePropChange = useCallback(
        async (val: CasinoFilterPropType | "") => {
            await navigateToSection(val as string, { filter: val });
        },
        [navigateToSection],
    );

    const onGameTagChange = useCallback(
        async (val: CasinoGameTag | "") => {
            await navigateToSection(val, { tag: val });
        },
        [navigateToSection],
    );

    const onGameProviderChange = useCallback(
        async (val: ProvidersEnum | "") => {
            navigateToSection(val, { provider: val });
        },
        [navigateToSection],
    );

    return {
        onGameTypeChange,
        onGameTagChange,
        onGamePropChange,
        onGameProviderChange,
    };
};
